import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from "react";
import "./RSSReader.css";

interface FeedItem {
    tag: string;
    title: string;
    link: string;
    pubDate: string;
    description: string;
    image?: string;
}

export interface RSSReaderHandle {
    /** Attempts to connect to the feed URL. Returns true on success. */
    Connect: (url: string) => Promise<boolean>;
}

interface RSSReaderProps {
    reloadDelayMs?: number;
}

const defaultReloadDelay = 500_000; // ms

const RSSReader = forwardRef<RSSReaderHandle, RSSReaderProps>(function RSSReader(
    { reloadDelayMs = defaultReloadDelay },
    ref
) {
    const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [url, setUrl] = useState<string | null>(null);

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useImperativeHandle(ref, () => ({
        Connect: async (candidateUrl: string) => {
            try {
                new URL(candidateUrl);
            } catch {
                return false;
            }

            const reachable = await tryReach(candidateUrl);
            if (!reachable) return false;

            setUrl(candidateUrl);
            return await parseRSS(candidateUrl);
        },
    }));

    useEffect(() => {
        if (!url) return undefined;

        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => parseRSS(url), reloadDelayMs);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        };
    }, [url, reloadDelayMs]);

    const tryReach = async (input: string): Promise<boolean> => {
        // Try direct fetch first, fall back to CORS proxy
        try {
            let res = await fetch(input, { method: "HEAD" });
            if (!res.ok || !isLikelyFeed(res.headers.get("content-type"))) {
                res = await fetch(input, { method: "GET" });
            }
            if (res.ok && isLikelyFeed(res.headers.get("content-type"))) return true;
        } catch { /* direct fetch blocked by CORS — try proxy */ }

        try {
            const proxied = `https://corsproxy.io/?${encodeURIComponent(input)}`;
            let res = await fetch(proxied, { method: "HEAD" });
            if (!res.ok || !isLikelyFeed(res.headers.get("content-type"))) {
                res = await fetch(proxied, { method: "GET" });
                if (!res.ok || !isLikelyFeed(res.headers.get("content-type"))) return false;
            }
            return true;
        } catch {
            return false;
        }
    };

    const isLikelyFeed = (ct: string | null): boolean => {
        if (!ct) return false;
        const v = ct.toLowerCase();
        return (
            v.includes("xml") ||
            v.includes("rss") ||
            v.includes("atom") ||
            v.includes("json")
        );
    };

    const parseRSS = async (input: string): Promise<boolean> => {
        setLoading(true);
        try {
            const text = await fetchWithFallback(input);
            if (text === null) return false;
            return parseRSSFromString(text);
        } catch {
            return false;
        } finally {
            setLoading(false);
        }
    };

    const fetchWithFallback = async (input: string): Promise<string | null> => {
        // Try direct fetch first, fall back to CORS proxy
        try {
            const res = await fetch(input);
            if (res.ok) return await res.text();
        } catch { /* direct fetch blocked by CORS — try proxy */ }

        try {
            const proxied = `https://corsproxy.io/?${encodeURIComponent(input)}`;
            const res = await fetch(proxied);
            if (res.ok) return await res.text();
        } catch { /* proxy also failed */ }

        return null;
    };

    const parsererror = "parsererror";

    /** JSON feeds like `{ items: [...] }` (e.g. CloudFront CMS); returns null if not that shape. */
    const tryParseJsonFeed = (text: string): FeedItem[] | null => {
        const trimmed = text.trim();
        if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
        try {
            const data = JSON.parse(text) as unknown;
            let rawItems: unknown[] | null = null;
            if (Array.isArray(data)) {
                rawItems = data;
            } else if (typeof data === "object" && data !== null && "items" in data) {
                const items = (data as {items: unknown}).items;
                if (Array.isArray(items)) rawItems = items;
            }
            if (rawItems === null) return null;

            type Row = {
                title: string;
                link: string;
                pubDate: string;
                description: string;
                tag: string;
                image?: string;
                pinned: boolean;
                sortTime: number;
            };

            const rows: Row[] = rawItems
                .map((row): Row | null => {
                    if (!row || typeof row !== "object") return null;
                    const o = row as Record<string, unknown>;
                    const title = String(o.title ?? "");
                    if (!title) return null;
                    const link = String(o.link ?? "");
                    const description = String(o.description ?? "");
                    const pubDate = String(o.publishedAt ?? o.pubDate ?? "");
                    const tag =
                        (typeof o.contentType === "string" && o.contentType) ||
                        (Array.isArray(o.tags) && o.tags[0] != null
                            ? String(o.tags[0])
                            : "");
                    const image =
                        typeof o.image === "string"
                            ? o.image
                            : typeof o.imageUrl === "string"
                              ? o.imageUrl
                              : undefined;
                    const pinned = Boolean(o.pinned);
                    const sortTime = pubDate ? Date.parse(pubDate) : 0;
                    return {
                        title,
                        link,
                        pubDate,
                        description,
                        tag,
                        image,
                        pinned,
                        sortTime,
                    };
                })
                .filter((x): x is Row => x !== null);

            rows.sort((a, b) => {
                if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
                return b.sortTime - a.sortTime;
            });

            return rows.map(
                ({title, link, pubDate, description, tag, image}): FeedItem => ({
                    title,
                    link,
                    pubDate,
                    description,
                    tag,
                    image,
                })
            );
        } catch {
            return null;
        }
    };

    const parseRSSFromString = (xmlString: string): boolean => {
        const fromJson = tryParseJsonFeed(xmlString);
        if (fromJson !== null) {
            if (fromJson.length === 0) {
                setFeedItems([]);
                return false;
            }
            setFeedItems(fromJson);
            return true;
        }

        try {
            const parser = new DOMParser();
            const xml = parser.parseFromString(xmlString, "application/xml");

            if (xml.querySelector(parsererror)) {
                return false;
            }

            let itemsRaw = Array.from(xml.getElementsByTagName("item"));
            if (itemsRaw.length === 0) {
                itemsRaw = Array.from(xml.getElementsByTagName("entry"));
            }

            const items: FeedItem[] = itemsRaw.map((item) => {
                const title =
                    item.getElementsByTagName("title")[0]?.textContent || "";
                const link =
                    item.getElementsByTagName("link")[0]?.textContent ||
                    item.getElementsByTagName("id")[0]?.textContent ||
                    "";
                const pubDate =
                    item.getElementsByTagName("pubDate")[0]?.textContent ||
                    item.getElementsByTagName("updated")[0]?.textContent ||
                    "";

                const descriptionRaw =
                    item.getElementsByTagName("description")[0]?.textContent ||
                    item.getElementsByTagName("summary")[0]?.textContent ||
                    "";
                const description = descriptionRaw.replace(/<img[^>]*>/gi, "");

                const media =
                    item.getElementsByTagName("media:content")[0]?.getAttribute("url") ||
                    item.getElementsByTagName("enclosure")[0]?.getAttribute("url") ||
                    undefined;
                const tag = item.getElementsByTagName("tag")[0]?.textContent || "";

                return { title, link, pubDate, description, image: media, tag };
            });

            if (items.length === 0) {
                setFeedItems([]);
                return false;
            } else {
                setFeedItems(items);
                return true;
            }
        } catch {
            setFeedItems([]);
            return false;
        }
    };

    if (loading) return <div className="rssreader-container">Loading...</div>;

    return (
        <div className="rssreader-container">
            <div className="rssreader-feed-list">
                {feedItems.map((item, index) => (
                    <div key={index} style={{ padding: "0" }}>
                        <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rssreader-link"
                        >
                            <div className="rssreader-card">
                                <div className="rssreader-card-top">
                                    <div className="rssreader-card-top-left">
                                        <div className="rssreader-title">{item.title}</div>
                                        <p className="rssreader-date">
                                            {new Date(item.pubDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div className="rssreader-card-top-right">
                                        <div className="rssreader-tag">{item.tag}</div>
                                    </div>
                                </div>
                                {item.image && (
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="rssreader-image"
                                    />
                                )}
                                <div className="rssreader-description">
                                    {item.description}
                                </div>
                            </div>
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
});

export default RSSReader;
